import { payPeriodParamsFromDriver } from './PayDatesPopover'
import { distributionsInMonth } from './engine/cashPolicy'
import { monthKeyFromT } from './engine/calendar'
import type { CaseInputs, History, Model, View } from './engine/types'
import { formatCurrency, formatNumber, formatSummaryMoney } from './projectionsFormat'

const PAYROLL_WAGES = 'is.payroll_wages'

const HOURLY_COMPONENT_KEYS = new Set([
  'is.hourly_fte_boh',
  'is.hourly_fte_foh',
  'is.avg_hourly_rate_boh',
  'is.avg_hourly_rate_foh',
])

type HistorySlice = Pick<History, 'nHistory' | 'values' | 'laborMeta'> | null

function getNumericValue(
  evaluated: Model | null,
  history: HistorySlice,
  view: View,
  key: string,
  t: number,
): number | null {
  if (t <= 0 && history) {
    const idx = history.nHistory - 1 + t
    const series = history.values[view]?.[key]
    if (series && idx >= 0 && idx < series.length) {
      const raw = series[idx]
      return typeof raw === 'number' ? raw : null
    }
  }
  const raw = evaluated?.values[view]?.[key]?.[t] ?? null
  return typeof raw === 'number' ? raw : null
}

function laborComponentValue(
  evaluated: Model | null,
  history: HistorySlice,
  view: View,
  key: string,
  t: number,
): number | null {
  const raw = getNumericValue(evaluated, history, view, key, t)
  if (raw == null && view === 'ga' && HOURLY_COMPONENT_KEYS.has(key)) {
    return 0
  }
  return raw
}

export function buildLaborBuildupHelp(options: {
  view: View
  t: number
  lastClosedMonth: string
  draft: CaseInputs
  evaluated: Model | null
  history: HistorySlice
  includeResidual?: boolean
}): string | null {
  const {
    view,
    t,
    lastClosedMonth,
    draft,
    evaluated,
    history,
    includeResidual = false,
  } = options

  if (view === 'consolidated') {
    return null
  }

  const payParams = payPeriodParamsFromDriver(
    draft.drivers[view]?.[PAYROLL_WAGES]?.by_type.labor_buildup?.params,
  )
  if (!payParams) {
    return null
  }

  const fteBoh = laborComponentValue(evaluated, history, view, 'is.hourly_fte_boh', t)
  const fteFoh = laborComponentValue(evaluated, history, view, 'is.hourly_fte_foh', t)
  const rateBoh = laborComponentValue(evaluated, history, view, 'is.avg_hourly_rate_boh', t)
  const rateFoh = laborComponentValue(evaluated, history, view, 'is.avg_hourly_rate_foh', t)
  const headcount = laborComponentValue(evaluated, history, view, 'is.salaried_headcount', t)
  const salary = laborComponentValue(evaluated, history, view, 'is.avg_salary_annual', t)

  if (
    fteBoh == null ||
    fteFoh == null ||
    rateBoh == null ||
    rateFoh == null ||
    headcount == null ||
    salary == null
  ) {
    return null
  }

  const monthKey = monthKeyFromT(lastClosedMonth, t)
  const payDates = distributionsInMonth(payParams.cadence, payParams.anchor_date, monthKey)
  if (payDates == null) {
    return null
  }

  const wagesOutput = getNumericValue(evaluated, history, view, PAYROLL_WAGES, t)
  let help =
    `${formatNumber(fteBoh)} hourly FTE BOH × ${formatCurrency(rateBoh)} × 80 h + ` +
    `${formatNumber(fteFoh)} FOH × ${formatCurrency(rateFoh)} × 80 h + ` +
    `${formatNumber(headcount)} salaried × ${formatSummaryMoney(salary)} ÷ 26, ` +
    `× ${payDates} pay dates`
  if (wagesOutput != null) {
    help += ` → ${formatSummaryMoney(wagesOutput)}`
  }

  if (includeResidual && history?.laborMeta?.residual) {
    const lastActualMonth = monthKeyFromT(lastClosedMonth, 0)
    const residualRaw = history.laborMeta.residual[view]?.[lastActualMonth]
    if (residualRaw != null && residualRaw !== '') {
      const residual = Number(residualRaw)
      if (Number.isFinite(residual) && residual !== 0) {
        help += `\nOther wage adjustments in history: ${formatSummaryMoney(residual)}`
      }
    }
  }

  return help
}

export function isLaborBuildupWagesRow(view: View, lineKey: string, draft: CaseInputs): boolean {
  return (
    lineKey === PAYROLL_WAGES &&
    view !== 'consolidated' &&
    draft.drivers[view]?.[PAYROLL_WAGES]?.active === 'labor_buildup'
  )
}
